/* eslint-disable no-magic-numbers , complexity */

import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { format, sub } from 'date-fns';

import { ForecastListItem, ReportTotal } from '@futrli-sdk/predict';

import { CATEGORY_NAME_INDEXES, REPORT_SECTION } from '../../../../../core/constants';
import { ConsolidatedForecastReport } from '../../../../../core/types';
import { forceLocalDate } from '../../../../../utils/dates';
import { getForecastKindDuration } from '../useLoadDailyCashFlowNumbers/utils';
import { multiplyNumberStrBy100 } from '../../../../../utils/number-string';
import { ROW_KIND } from '../../../reports/data-grid/constants';
import { TotalRow } from '../../../reports/data-grid/types';
import { useLoadDailyCashFlowNumbers } from '../useLoadDailyCashFlowNumbers/useLoadDailyCashFlowNumbers';
import { usePredictManagementContext } from '@futrli/predict/predict-management/context/predict-management-context';

export const useTotalCashPredictRow = (
  workspaceId: string,
  organisation: { id: string; isConsolidation: boolean },
  forecast: Pick<ForecastListItem, 'id' | 'kind' | 'forecast_from_date'> | null | undefined,
  forecastReport: ConsolidatedForecastReport | null | undefined,
  showActuals: boolean
) => {
  const bankAccountRef = forecastReport?.data.chart_items?.cash_position.ref;
  const { financialConfig } = usePredictManagementContext();

  const fromDate = (() => {
    if (showActuals) {
      if (financialConfig?.current_year.start) {
        return format(sub(forceLocalDate(financialConfig.current_year.start), { years: 2 }), 'yyyy-MM-dd');
      }
    } else {
      return forecast?.forecast_from_date || undefined;
    }
  })();

  const toDate = (() => {
    if (showActuals) {
      return 6;
    } else if (forecast) {
      return getForecastKindDuration(forecast.kind);
    }
  })();

  const { data: dailyData, isLoading } = useLoadDailyCashFlowNumbers(
    workspaceId,
    organisation,
    forecast?.id,
    fromDate,
    toDate,
    bankAccountRef
  );

  // Generate monthly graph first - we can do this without any extra data from backend
  const monthlyGraph = useMemo((): TotalRow | null => {
    if (forecastReport) {
      const data = forecastReport.data;
      const chartData = data.chart_items?.cash_position;

      if (chartData && data.by_ref[chartData.ref]) {
        const allRowValues = data.by_ref[chartData.ref].row;
        const filteredRowValues = showActuals ? allRowValues : allRowValues.slice(chartData.forecast_start_index); // Exclude actuals

        const account = {
          canonical_category: CATEGORY_NAME_INDEXES.bank_accounts,
          index: CATEGORY_NAME_INDEXES.bank_accounts,
          name: '',
          period_data: filteredRowValues,
          type: ROW_KIND.total,
          period_to_date: data.by_ref[chartData.ref].period_to_date || ''
        } as ReportTotal;

        const cashBalanceSuperTitle = (
          <FormattedMessage id="predict.accounts.cash_balance.supertitle" defaultMessage="ALL BANK ACCOUNTS" />
        );
        const cashBalanceTitle = (
          <FormattedMessage id="predict.accounts.cash_balance.title" defaultMessage="Cash position" />
        );

        return {
          section: REPORT_SECTION.BalanceSheet,
          isBold: true,
          kind: ROW_KIND.total,
          name: cashBalanceTitle,
          supertitle: cashBalanceSuperTitle,
          account,
          disableLargeSize: false,
          padding: false,
          childCount: 0,
          level: 0,
          isFirst: true,
          isLast: true
        };
      }
    }

    return null;
  }, [forecastReport, showActuals]);

  // If we have daily data, merge that into the monthly graph
  const dailyGraph = useMemo((): TotalRow | null => {
    if (monthlyGraph && dailyData && bankAccountRef) {
      const columnMeta = dailyData.field_meta[bankAccountRef]?.column_meta || [];
      const fieldValues = dailyData.fields[bankAccountRef] || [];

      // Keep the daily series within the report's last rendered month. The daily request window can be
      // longer than the forecast (e.g. "This FY + 1/2 year"), which would otherwise give the chart more
      // date labels than there is data, shifting the labels away from the values.
      const reportColumns = forecastReport?.data.columns || [];
      const lastMonth = reportColumns[reportColumns.length - 1]?.substring(0, 7);

      const dailyColumns: string[] = [];
      const dailyValues: string[] = [];
      columnMeta.forEach((meta, index) => {
        if ((!lastMonth || meta.start_date.substring(0, 7) <= lastMonth) && fieldValues[index] !== undefined) {
          dailyColumns.push(meta.start_date);
          dailyValues.push(multiplyNumberStrBy100(fieldValues[index]));
        }
      });

      // Without any daily points in range fall back to the monthly graph (avoids an empty daily chart)
      if (!dailyColumns.length) {
        return null;
      }

      return {
        ...monthlyGraph,
        dailyColumns,
        dailyData: dailyValues
      };
    }
    return null;
  }, [monthlyGraph, dailyData, bankAccountRef, forecastReport]);

  return {
    totalCashPredictRow: dailyGraph || monthlyGraph,
    isLoading
  };
};
